import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faPen, faTrash} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';

import {FieldService} from '../../../services/field.service';
import {ClusterService} from '../../../services/cluster.service';
import {RegisterService} from '../../../services/register.service';
import {AuthService} from '../../../services/auth.service';

@Component({
    selector: 'app-field-config',
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule],
    templateUrl: './field-config.component.html',
    styleUrl: './field-config.component.css'
})
export class FieldConfigComponent implements OnInit {
  registerId!: number;
  register: any;
  fields: any[] = [];
  clusters: any[] = [];
  steps: any[] = [];
  isAdminUser: boolean = false;

  fieldForm: FormGroup;
  isEditMode: boolean = false;
  editingFieldId: number | null = null;

  fieldTypes = [
    { label: 'Text', value: 'text' },
    { label: 'Textarea', value: 'textarea' },
    { label: 'Number', value: 'number' },
    { label: 'Date', value: 'date' },
    { label: 'Datetime', value: 'datetime' },
    { label: 'Dropdown', value: 'dropdown' },
    { label: 'File', value: 'file' },
    { label: 'Image', value: 'image' },
    { label: 'Checkbox', value: 'checkbox' },
    { label: 'Radio', value: 'radio' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private fieldService: FieldService,
    private clusterService: ClusterService,
    private registerService: RegisterService,
    private auth: AuthService,
    private toastr: ToastrService,
    library: FaIconLibrary
  ) {
    library.addIcons(faTrash, faPen);
    this.fieldForm = this.fb.group({
      name: ['', Validators.required],
      data_type: ['text', Validators.required],
      step_number: [1, Validators.required],
      sequence: [0],
      is_mandatory: [false],
      is_unique: [false],
      min_length: [''],
      max_length: [''],
      is_numeric: [false],
      is_decimal: [false],
      cluster: [null],
      help_text: [''],
      is_searchable: [false],
      is_filterable: [false],
      is_active: [true]
    });

    this.fieldForm.get('data_type')?.valueChanges.subscribe(value => {
      const clusterControl = this.fieldForm.get('cluster');
      if (['dropdown', 'radio', 'checkbox'].includes(value)) {
        clusterControl?.setValidators([Validators.required]);
      } else {
        clusterControl?.clearValidators();
        clusterControl?.setValue(null);
      }
      clusterControl?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    const roles = this.getUserRoles();
      const isPlatformSuperuser = this.auth.isSuperUser();
      this.isAdminUser = roles.includes('Admin') && !isPlatformSuperuser;
      if (isPlatformSuperuser) {
      this.router.navigate(['/dashboard']);
      return;
    }
    if (!this.isAdminUser) {
      this.router.navigate(['/visitor-management/select-register']);
      return;
    }
    this.route.params.subscribe(params => {
      this.registerId = Number(params['id']);
      this.loadRegister();
      this.loadFields();
      this.loadClusters();
    });
  }

  private getUserRoles(): string[] {
    const userInfo = this.auth.getCurrentUserInfo();
    const role = userInfo?.role;
    if (Array.isArray(role)) {
      return role;
    }
    return role ? [role] : [];
  }

  loadRegister(): void {
    this.registerService.getRegister(this.registerId).subscribe({
      next: (res: any) => {
        this.register = res;
        this.steps = Array.isArray(res.steps_config) && res.steps_config.length
          ? res.steps_config
          : [{ step: 1, label: 'Step 1' }];
      }
    });
  }

  loadFields(): void {
    this.fieldService.listFieldsByRegister(this.registerId).subscribe({
      next: (res: any) => {
        this.fields = res || [];
      }
    });
  }

  loadClusters(): void {
    this.clusterService.getClusters(1, 200).subscribe({
      next: (res: any) => {
        this.clusters = res?.results || [];
      }
    });
  }

  shouldShowCluster(): boolean {
    const type = this.fieldForm.get('data_type')?.value;
    return ['dropdown', 'radio', 'checkbox'].includes(type);
  }

  submit(): void {
    if (this.fieldForm.invalid) {
      this.fieldForm.markAllAsTouched();
      return;
    }
    const formValue = this.fieldForm.value;
    const payload = {
      register: this.registerId,
      ...formValue,
      min_length: formValue.min_length ? Number(formValue.min_length) : null,
      max_length: formValue.max_length ? Number(formValue.max_length) : null,
      sequence: formValue.sequence ? Number(formValue.sequence) : 0,
      cluster: formValue.cluster || null
    };
    if (this.isEditMode && this.editingFieldId) {
      this.fieldService.updateField(this.editingFieldId, payload).subscribe({
        next: () => {
          this.toastr.success('Field updated successfully.', 'Success');
          this.resetForm();
          this.loadFields();
        }
      });
    } else {
      this.fieldService.createField(payload).subscribe({
        next: () => {
          this.toastr.success('Field added successfully.', 'Success');
          this.resetForm();
          this.loadFields();
        }
      });
    }
  }

  editField(field: any): void {
    this.isEditMode = true;
    this.editingFieldId = field.id;
    this.fieldForm.patchValue({
      name: field.name,
      data_type: field.data_type,
      step_number: field.step_number,
      sequence: field.sequence,
      is_mandatory: field.is_mandatory,
      is_unique: field.is_unique,
      min_length: field.min_length,
      max_length: field.max_length,
      is_numeric: field.is_numeric,
      is_decimal: field.is_decimal,
      cluster: field.cluster,
      help_text: field.help_text,
      is_searchable: field.is_searchable,
      is_filterable: field.is_filterable,
      is_active: field.is_active
    });
  }

  deleteField(fieldId: number): void {
    this.fieldService.deleteField(fieldId).subscribe({
      next: () => {
        this.toastr.success('Field removed.', 'Success');
        this.loadFields();
      }
    });
  }

  resetForm(): void {
    this.isEditMode = false;
    this.editingFieldId = null;
    this.fieldForm.reset({
      name: '',
      data_type: 'text',
      step_number: 1,
      sequence: 0,
      is_mandatory: false,
      is_unique: false,
      min_length: '',
      max_length: '',
      is_numeric: false,
      is_decimal: false,
      cluster: null,
      help_text: '',
      is_searchable: false,
      is_filterable: false,
      is_active: true
    });
  }

  goBack(): void {
    this.router.navigate(['/visitor-management/registers']);
  }
}
